import type { DocumentType } from '@kmdx/contracts';
import { db, useLive } from './db';
import { DOCUMENT_LABELS } from './labels';
import type { EventRecord, OdometerReading, PhotoRecord, ReshootTask, StopDto } from './types';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  КОЕ ДОКУМЕНТ СПИРА СЛЕДВАЩАТА СТЪПКА
 *
 *  Правилото на клиента: всеки документ се потвърждава от шофьора, преди да
 *  продължи с каквото и да е. Затова документите не са „по желание" накрая на
 *  курса, а стъпки в поредицата:
 *
 *    товарене:      … → ЧМР (подписи по е-ЧМР или снимка на хартиената) → Натоварих
 *    разтоварване:  … → Разтоварих → снимки, които клиентът иска → Подпис
 *
 *  Снимка се брои, след като шофьорът я е видял и потвърдил. Офисът я
 *  преглежда по-късно; върната от него снимка става задача „снимай пак"
 *  и спира всички стъпки, докато не се заснеме наново.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Документите, които се снимат като лист хартия — може да са няколко страници. */
const PAPER: DocumentType[] = ['cmr', 'pod', 'delivery_note', 'packing_list', 'weighbridge', 'customs', 'other'];

export function isPaper(type: DocumentType): boolean {
  return PAPER.includes(type);
}

/** Снимка, която шофьорът е потвърдил. Старите (отпреди потвърждението) се броят. */
export function isConfirmed(photo: PhotoRecord): boolean {
  return photo.confirmedAt !== null;
}

export interface DocNeed {
  type: DocumentType;
  label: string;
  done: boolean;
}

/**
 * Заявките, чиято ЧМР е подписана електронно на товарене — по сървъра или по
 * събитията на този телефон (подписите може още да не са стигнали до офиса).
 */
export function electronicOrders(stops: StopDto[], events: EventRecord[]): Set<string> {
  const ids = new Set<string>();
  for (const stop of stops) {
    if (!stop.orderId) continue;
    if (stop.cmrElectronic) ids.add(stop.orderId);
    if (stop.kind === 'pickup' && events.some((e) => e.orderStopId === stop.id && e.type === 'ecmr_signed')) {
      ids.add(stop.orderId);
    }
  }
  return ids;
}

/**
 * Снимките, които разтоварването иска преди подписа на получателя. Подписът
 * („Разписка за доставка") е отделна стъпка; ЧМР не се снима, когато е
 * електронна — подписът на получателя я довършва.
 */
export function deliveryDocNeeds(stop: StopDto, photos: PhotoRecord[], electronic: Set<string>): DocNeed[] {
  if (stop.kind !== 'delivery') return [];
  return stop.requiredDocuments
    .filter((type) => type !== 'pod')
    .filter((type) => !(type === 'cmr' && stop.orderId && electronic.has(stop.orderId)))
    .map((type) => ({
      type,
      label: DOCUMENT_LABELS[type],
      done: photos.some((photo) => photo.orderStopId === stop.id && photo.type === type && isConfirmed(photo)),
    }));
}

/** ЧМР на товарене: подписите по е-ЧМР или потвърдена снимка на хартиената. */
export function pickupCmrDone(stop: StopDto, photos: PhotoRecord[], events: EventRecord[]): 'ecmr' | 'paper' | null {
  if (events.some((e) => e.orderStopId === stop.id && e.type === 'ecmr_signed')) return 'ecmr';
  if (photos.some((photo) => photo.orderStopId === stop.id && photo.type === 'cmr' && isConfirmed(photo))) return 'paper';
  return null;
}

/**
 * Какво липсва по целия курс — за „Приключи курса". Същите правила като
 * стъпките, плюс подписа на получателя на всяко разтоварване.
 */
export function useMissingDocuments(tripId: string): string[] {
  return useLive(
    async () => {
      const stops = await db.stops.where('tripId').equals(tripId).toArray();
      const photos = await db.photos.where('tripId').equals(tripId).toArray();
      const events = await db.events.where('tripId').equals(tripId).toArray();
      const electronic = electronicOrders(stops, events);
      const missing = new Set<string>();
      for (const stop of stops) {
        if (stop.status === 'failed') continue;
        if (stop.kind === 'pickup' && !pickupCmrDone(stop, photos, events) && stop.status !== 'completed') {
          missing.add(DOCUMENT_LABELS.cmr);
        }
        for (const need of deliveryDocNeeds(stop, photos, electronic)) {
          if (!need.done) missing.add(need.label);
        }
        if (stop.kind === 'delivery' && stop.requiredDocuments.includes('pod')) {
          const signed = photos.some((p) => p.orderStopId === stop.id && p.type === 'pod');
          if (!signed) missing.add(DOCUMENT_LABELS.pod);
        }
      }
      return [...missing];
    },
    [tripId],
    [],
  );
}

/**
 * Отворените задачи „снимай пак". Задача, за която на телефона вече има нова
 * снимка (още неизпратена или чакаща преглед), не се показва — шофьорът си е
 * свършил работата, сървърът ще я затвори при качването.
 */
export function useOpenTasks(): ReshootTask[] {
  return useLive(
    async () => {
      const tasks = await db.tasks.toArray();
      if (tasks.length === 0) return [];
      const redone = await db.photos.where('replacesDocumentId').anyOf(tasks.map((t) => t.documentId)).toArray();
      const done = new Set(redone.map((p) => p.replacesDocumentId));
      return tasks
        .filter((task) => !done.has(task.documentId))
        .sort((a, b) => (a.requestedAt ?? '').localeCompare(b.requestedAt ?? ''));
    },
    [],
    [],
  );
}

/** Показанието на километража на телефона — докато не е тръгнало със събитието си. */
export function useOdometerReading(tripId: string | null, which: 'start' | 'end'): OdometerReading | null {
  return useLive(
    async () => {
      if (!tripId) return null;
      const row = await db.meta.get(`odometer:${tripId}:${which}`);
      return (row?.value as OdometerReading | undefined) ?? null;
    },
    [tripId, which],
    null,
  );
}
